import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Phone, PhoneIncoming, PhoneOutgoing, RefreshCw, Plus, X, Trash2,
  Lock, CheckCircle2, Pencil, Volume2, MessageSquare, Pill,
} from "lucide-react";

const BLANK = {
  person_name: "", phone: "", email: "", subject: "",
  agent_script: "", internal_note: "", ref_type: "", ref_id: "",
};

const only10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);
const prettyPhone = (p) => {
  const d = only10(p);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || "n/a");
};
const when = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "n/a";

const INPUT =
  "w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope text-paw-text dark:text-white focus:outline-none focus:border-paw-accent";
const AREA =
  "w-full px-4 py-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope text-paw-text dark:text-white focus:outline-none focus:border-paw-accent resize-y";
const LABEL = "font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider";
const CARD = "bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl";
const GHOST =
  "flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text dark:text-white transition-colors";

export default function AdminCallNotes() {
  const [notes, setNotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("open");
  const [direction, setDirection] = useState("all");
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [n, l] = await Promise.all([
        api.get("/admin/call-notes"),
        api.get("/admin/medication-leads"),
      ]);
      setNotes(n.data || []);
      setLeads(l.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(""); setForm({ ...BLANK }); };
  const openEdit = (n) => {
    setEditing(n.note_id);
    setForm({
      person_name: n.person_name || "", phone: n.phone || "", email: n.email || "",
      subject: n.subject || "", agent_script: n.agent_script || "",
      internal_note: n.internal_note || "", ref_type: n.ref_type || "", ref_id: n.ref_id || "",
    });
  };
  const close = () => { if (!saving) { setForm(null); setEditing(""); } };

  const pickLead = (leadId) => {
    if (!leadId) { setForm((f) => ({ ...f, ref_type: "", ref_id: "" })); return; }
    const ld = leads.find((x) => x.lead_id === leadId);
    setForm((f) => ({
      ...f,
      ref_type: "medication_lead", ref_id: leadId,
      person_name: f.person_name || ld?.full_name || "",
      phone: f.phone || ld?.phone || "",
      email: f.email || ld?.email || "",
      subject: f.subject || (ld?.medication ? `Missing details for ${ld.medication}` : ""),
    }));
  };

  const save = async () => {
    if (only10(form.phone).length !== 10) {
      toast.error("A full 10-digit phone number is required. It is how Savannah finds this note.");
      return;
    }
    if (!form.agent_script.trim()) {
      toast.error("Write what Savannah should say. Without it she has nothing to read out.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/call-notes/${editing}`, form);
        toast.success("Note updated");
      } else {
        await api.post("/admin/call-notes", form);
        toast.success("Note saved. Savannah will read it when they call back");
      }
      setForm(null); setEditing(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setStatusOf = async (n, next) => {
    try {
      await api.patch(`/admin/call-notes/${n.note_id}`, { status: next });
      toast.success(next === "closed" ? "Note closed" : "Note reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (n) => {
    try { await api.delete(`/admin/call-notes/${n.note_id}`); toast.success("Note deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const shown = useMemo(
    () => notes.filter((n) => (status === "all" || n.status === status) &&
                              (direction === "all" || n.direction === direction)),
    [notes, status, direction]
  );
  const counts = useMemo(() => ({
    open: notes.filter((n) => n.status === "open").length,
    inbound: notes.filter((n) => n.direction === "inbound").length,
    unread: notes.filter((n) => n.direction === "outbound" && !n.read_count).length,
  }), [notes]);

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-5xl" data-testid="admin-call-notes">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Voice</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Outbound Calls</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2 max-w-2xl">
            Leave a note against a phone number before or after you call someone. When they ring
            1-888-827-2717 and read that number back, Savannah finds the note and answers their
            matter instead of starting from scratch. Messages she takes on the phone land here too.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className={GHOST} data-testid="call-notes-refresh">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent text-white hover:opacity-90 font-manrope text-sm transition-opacity"
            data-testid="call-notes-new"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> New note
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Open", value: counts.open, icon: Phone },
          { label: "Not read yet", value: counts.unread, icon: Volume2 },
          { label: "Messages taken", value: counts.inbound, icon: MessageSquare },
        ].map((s) => (
          <div key={s.label} className={`${CARD} p-4`}>
            <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40">
              <s.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-manrope text-xs uppercase tracking-wider">{s.label}</span>
            </div>
            <p className="font-outfit font-bold text-2xl mt-1 text-paw-text dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["open", "closed", "all"].map((f) => (
          <button
            key={f} onClick={() => setStatus(f)}
            className={`px-3 h-8 rounded-md text-xs font-manrope transition-colors ${
              status === f ? "bg-paw-accent text-white"
                : "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/70"}`}
            data-testid={`call-notes-status-${f}`}
          >{f}</button>
        ))}
        <span className="w-px bg-paw-border dark:bg-white/10 mx-1" />
        {[["all", "both"], ["outbound", "my notes"], ["inbound", "messages taken"]].map(([v, lbl]) => (
          <button
            key={v} onClick={() => setDirection(v)}
            className={`px-3 h-8 rounded-md text-xs font-manrope transition-colors ${
              direction === v ? "bg-paw-accent text-white"
                : "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/70"}`}
            data-testid={`call-notes-dir-${v}`}
          >{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : shown.length === 0 ? (
        <div className={`${CARD} p-12 text-center font-manrope text-paw-text-muted dark:text-white/40`}>
          <Phone className="w-8 h-8 mx-auto mb-3 text-paw-text-muted/40 dark:text-white/20" strokeWidth={1.5} />
          Nothing here yet. Leave a note before you call someone and Savannah will pick it up.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((n) => {
            const inbound = n.direction === "inbound";
            const Icon = inbound ? PhoneIncoming : PhoneOutgoing;
            return (
              <div key={n.note_id} className={`${CARD} p-5`} data-testid={`call-note-${n.note_id}`}>
                <div className="flex items-start gap-4">
                  <Icon
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${inbound ? "text-sky-700 dark:text-sky-400" : "text-emerald-700 dark:text-emerald-400"}`}
                    strokeWidth={1.5}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className={`font-manrope text-xs font-semibold uppercase tracking-wider ${inbound ? "text-sky-700 dark:text-sky-400" : "text-emerald-700 dark:text-emerald-400"}`}>
                        {inbound ? "Message taken by Savannah" : "Note for Savannah"}
                      </p>
                      {n.status === "closed" && (
                        <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">· Closed</span>
                      )}
                      {n.ref_type === "medication_lead" && (
                        <span className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 flex items-center gap-1">
                          <Pill className="w-3 h-3" strokeWidth={1.5} /> linked to a medication request
                        </span>
                      )}
                    </div>

                    <p className="font-outfit font-semibold text-base text-paw-text dark:text-white">
                      {n.person_name || "Unnamed caller"}
                      <span className="font-manrope font-normal text-sm text-paw-text-secondary dark:text-white/60"> · {prettyPhone(n.phone)}</span>
                    </p>
                    {n.subject && (
                      <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/70 mt-1">{n.subject}</p>
                    )}

                    {n.agent_script && (
                      <div className="mt-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3">
                        <p className="font-manrope text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mb-1">
                          <Volume2 className="w-3 h-3" strokeWidth={1.5} /> Savannah reads this out
                        </p>
                        <p className="font-manrope text-sm text-paw-text dark:text-white whitespace-pre-wrap">{n.agent_script}</p>
                      </div>
                    )}

                    {n.message && (
                      <div className="mt-3 rounded-lg border border-sky-500/20 bg-sky-500/10 p-3">
                        <p className="font-manrope text-xs font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-400 mb-1">What they said</p>
                        <p className="font-manrope text-sm text-paw-text dark:text-white whitespace-pre-wrap">{n.message}</p>
                      </div>
                    )}

                    {n.internal_note && (
                      <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                        <p className="font-manrope text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1 mb-1">
                          <Lock className="w-3 h-3" strokeWidth={1.5} /> Private, never spoken on a call
                        </p>
                        <p className="font-manrope text-sm text-paw-text dark:text-white whitespace-pre-wrap">{n.internal_note}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 font-manrope text-xs text-paw-text-muted dark:text-white/40">
                      <span>Created {when(n.created_at)}{n.created_by ? ` by ${n.created_by}` : ""}</span>
                      {!inbound && (
                        <span>{n.read_count ? `Read out ${n.read_count}×, last ${when(n.last_read_at)}` : "Not read on a call yet"}</span>
                      )}
                      {n.email && <span>{n.email}</span>}
                      {n.outcome && <span className="text-paw-text-secondary dark:text-white/60">{n.outcome}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!inbound && (
                      <button onClick={() => openEdit(n)} title="Edit"
                        className="p-2 rounded-lg hover:bg-black/[0.06] dark:hover:bg-white/10 text-paw-text-secondary dark:text-white/70 transition-colors"
                        data-testid={`call-note-edit-${n.note_id}`}>
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                    <button onClick={() => setStatusOf(n, n.status === "open" ? "closed" : "open")}
                      title={n.status === "open" ? "Close" : "Reopen"}
                      className="p-2 rounded-lg hover:bg-black/[0.06] dark:hover:bg-white/10 text-paw-text-secondary dark:text-white/70 transition-colors"
                      data-testid={`call-note-toggle-${n.note_id}`}>
                      <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(n)} title="Delete"
                      className="p-2 rounded-lg hover:bg-red-500/10 text-red-700 dark:text-red-400 transition-colors"
                      data-testid={`call-note-delete-${n.note_id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8" onClick={close}>
          <div className={`${CARD} w-full max-w-xl p-6 space-y-4`} onClick={(e) => e.stopPropagation()} data-testid="call-note-form">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-outfit font-bold text-xl text-paw-text dark:text-white">
                  {editing ? "Edit note" : "New call note"}
                </h2>
                <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-1">
                  Savannah matches on the last 10 digits of the number.
                </p>
              </div>
              <button onClick={close} className="p-2 rounded-lg hover:bg-black/[0.06] dark:hover:bg-white/10 text-paw-text-secondary dark:text-white/70">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            {!editing && leads.length > 0 && (
              <div>
                <label className={LABEL}>Link to a medication request (optional)</label>
                <select value={form.ref_id} onChange={(e) => pickLead(e.target.value)} className={`${INPUT} mt-1`} data-testid="call-note-lead">
                  <option value="">Not linked</option>
                  {leads.map((l) => (
                    <option key={l.lead_id} value={l.lead_id}>
                      {(l.full_name || l.email || "Unnamed")} · {l.medication || "no medication"} ({l.status})
                    </option>
                  ))}
                </select>
                <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">
                  Linking lets Savannah write the strength straight onto the request while they are still on the line.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Who</label>
                <input value={form.person_name} onChange={(e) => set("person_name", e.target.value)} placeholder="Jo Stro" className={`${INPUT} mt-1`} data-testid="call-note-name" />
              </div>
              <div>
                <label className={LABEL}>Phone (required)</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(813) 310-7114" className={`${INPUT} mt-1`} data-testid="call-note-phone" />
              </div>
            </div>

            <div>
              <label className={LABEL}>Email (optional)</label>
              <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="them@example.com" className={`${INPUT} mt-1`} data-testid="call-note-email" />
            </div>

            <div>
              <label className={LABEL}>What the call was about</label>
              <input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Missing strength for Zenrelia" className={`${INPUT} mt-1`} data-testid="call-note-subject" />
            </div>

            <div>
              <label className={LABEL}>What Savannah should say</label>
              <textarea rows={4} value={form.agent_script} onChange={(e) => set("agent_script", e.target.value)}
                placeholder="Thank them for calling back, then ask for the strength printed on the prescription label."
                className={`${AREA} mt-1`} data-testid="call-note-script" />
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">
                She reads this to the caller word for word, so write it the way you would say it.
              </p>
            </div>

            <div>
              <label className={`${LABEL} flex items-center gap-1`}><Lock className="w-3 h-3" strokeWidth={1.5} /> Private note (never spoken)</label>
              <textarea rows={2} value={form.internal_note} onChange={(e) => set("internal_note", e.target.value)}
                placeholder="Anything the caller must not hear."
                className={`${AREA} mt-1`} data-testid="call-note-internal" />
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">
                This is never sent to the voice layer. Anyone holding that handset would hear whatever Savannah reads, so keep sensitive detail here.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={close} className={GHOST} disabled={saving}>Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-accent text-white hover:opacity-90 font-manrope text-sm transition-opacity disabled:opacity-60"
                data-testid="call-note-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editing ? "Save changes" : "Save note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
